import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, ChevronDown, ChevronRight, Crosshair, Eye, History, ImagePlus, Layers, MessageSquare, Minimize2, ScanSearch, Send, SlidersHorizontal, Sparkles, SquarePen, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useClerkAuth } from "@/hooks/useClerkAuth";
import { SIDEBAR_BRAND_LOGO_SRC } from "@/constants/sidebarBrandLogo";
import { cn } from "@/lib/utils";
import {
  STRATEGY_RD_COACH_CATALOG_BADGE,
  STRATEGY_RD_COACH_DISPLAY_EXPANSION,
  STRATEGY_RD_COACH_DISPLAY_NAME,
  type StrategyRdCoachActivity,
  type StrategyRdCoachChip,
  type StrategyRdCoachProposal,
  type StrategyRdCoachStyle,
  type StrategyRdCoachThreadSummary,
  type StrategyRdCoachUiMessage,
  type StrategyRdCoachUsage,
} from "@/types/strategyRdCoach";
import { strategyRdBrandActionButtonClassName } from "../strategyRdCardShell";
import { statsCardHeadlineClassName } from "../../Statistics/components/statisticsCardHeadline";
import { StrategyRdCoachConfirmApply } from "./StrategyRdCoachConfirmApply";
import { StrategyRdCoachLiveActivity, StrategyRdCoachTurnTraceView } from "./StrategyRdCoachActivity";
import { StrategyRdCoachLabActionCard } from "./StrategyRdCoachLabActionCard";
import { StrategyRdCoachStrategyCard } from "./StrategyRdCoachStrategyCard";
import { strategyRdCoachReplyParts } from "@/utils/strategyRdCoachReplySections";
import type { StrategyRdCoachChartFocus } from "./useStrategyRdCoach";
import type { StrategyRdCoachAttachment } from "@/utils/strategyRdCoachImages";
import { formatTradeDateTimeForDisplay } from "@/utils/displayDateTime";
import { StrategyRdCoachThreads } from "./StrategyRdCoachThreads";
import type { StrategyRdCatalog } from "@/types/strategyRd";
import {
  strategyRdCoachChipForDisplay,
  strategyRdCoachChipsExceptAsked,
  strategyRdCoachChipsForTurn,
  strategyRdCoachOpenedBacktestLabel,
  type StrategyRdCoachOpenedBacktest,
} from "@/utils/strategyRdCoachLabStage";
import {
  strategyRdCoachReplyForDisplay,
  strategyRdCoachUserTextForDisplay,
} from "@/utils/strategyRdCoachReply";
import type { StrategyRdSessionPickerGroup } from "@/utils/strategyRdConcepts";

/** Catalog names and settings: a quiet neutral chip, so a reply reads as text, not as a sea of colour. */
const COACH_TERM_CLASS =
  "rounded-[4px] !border !border-solid !border-border bg-muted/60 !px-1 !py-px font-[family-name:var(--font-primary)] text-[0.8125rem] font-medium not-italic text-gray-600 dark:text-foreground";
/** Bold in replies: a weight step on the darker grey, never black. */
const COACH_LABEL_CLASS = "font-[family-name:var(--font-primary)] !font-semibold not-italic text-gray-600 dark:text-foreground";
/** Reading copy in the chat: the app's muted grey body text. */
const COACH_BODY_TEXT_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-6 text-gray-500 dark:text-muted-foreground";
/** Card and section titles in the chat: brand title colour, never black. */
const COACH_TITLE_CLASS =
  "font-[family-name:var(--font-primary)] !text-[15px] !font-medium leading-6 text-[color:var(--brand-tropical-surf)]";
/** Small captions (labels over chip rows, notes): light muted grey. */
const COACH_CAPTION_CLASS = "font-[family-name:var(--font-primary)] text-xs leading-5 text-gray-400 dark:text-muted-foreground";

/**
 * One chip style for every clickable suggestion in the chat (follow-ups, opened-backtest
 * actions, style seeds, common questions): the app's outline-brand button, primary font,
 * sentence case, wraps when long.
 */
const COACH_CHIP_CLASS = cn(
  strategyRdBrandActionButtonClassName,
  "h-auto min-h-8 max-w-full justify-start gap-1.5 !whitespace-normal !px-3 !py-1.5 text-left",
  "!font-[family-name:var(--font-primary)] !text-[0.8125rem] !font-normal !leading-5 !normal-case !tracking-normal",
  "[&>span]:!whitespace-normal [&>span]:!normal-case [&>span]:!tracking-normal"
);

/** Time-of-day greeting, same wording as the Dashboard banner. */
/** Empty chat about an opened backtest: questions TES can answer from it straight away. */
const BACKTEST_CHAT_PROMPTS = [
  "How did this backtest do?",
  "Why are the losing trades losing?",
  "Does it work on other markets?",
  "What should I change next?",
] as const;

function timeOfDayGreeting(now = new Date()): string {
  const h = now.getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  return "Good evening";
}

/** Example asks cycled in the empty composer — built from the style seeds TES already offers. */
function rotatingPromptsFromStyles(styles: StrategyRdCoachStyle[]): string[] {
  const fromStyles = styles.slice(0, 8).map((st) => `Build me a ${st.name} strategy`);
  return fromStyles.length > 0 ? fromStyles : ["Build me a session ORB strategy for the London open"];
}

function CoachTerm({ children }: { children?: React.ReactNode }) {
  return <span className={COACH_TERM_CLASS}>{children}</span>;
}

function CoachLabel({ children }: { children?: React.ReactNode }) {
  return <span className={COACH_LABEL_CLASS}>{children}</span>;
}

type Props = {
  width: number;
  onResizeStart: (clientX: number) => void;
  onMinimize: () => void;
  bootstrapping: boolean;
  usage: StrategyRdCoachUsage | null;
  styles: StrategyRdCoachStyle[];
  threads: StrategyRdCoachThreadSummary[];
  sessionId: string | null;
  messages: StrategyRdCoachUiMessage[];
  suggestedChips: StrategyRdCoachChip[];
  pendingProposal: StrategyRdCoachProposal | null;
  /** Screenshots to send with the next message. */
  attachments?: StrategyRdCoachAttachment[];
  onAddImages?: (files: File[]) => void;
  onRemoveAttachment?: (id: string) => void;
  /** "Ask TES about this" — the candle sent with the next message. */
  chartFocus?: StrategyRdCoachChartFocus | null;
  onClearChartFocus?: () => void;
  /** Result cards: open a TES backtest in the Lab / next to its original. */
  onOpenHypothesis?: (hypothesisId: string) => void;
  onCompareHypothesis?: (hypothesisId: string) => void;
  timeZone?: string;
  /** Lab docks the chat on the left (Quant-style): the resize handle sits on the right edge. */
  dock?: "left" | "right";
  input: string;
  onInputChange: (v: string) => void;
  sending: boolean;
  /** What TES is doing while ``sending`` (live steps). */
  activity?: StrategyRdCoachActivity | null;
  intakeBusy: boolean;
  confirmingPatch: boolean;
  quotaBlocked: boolean;
  threadsBusy: boolean;
  pendingRestore: boolean;
  /** An opened backtest with no chat yet: the empty chat is about it (title, prompts). */
  backtestChat?: { id: string; name: string } | null;
  /** A backtest opened from history: scroll to the reply that ran it; its actions replace
   *  the last turn's chips. */
  openedBacktest?: StrategyRdCoachOpenedBacktest | null;
  openedBacktestChips?: StrategyRdCoachChip[];
  disclaimer?: string | null;
  orientationChips?: StrategyRdCoachChip[];
  /** Orientation "Start a strategy": show the style pills above the input. */
  intakeOpen?: boolean;
  onCloseIntake?: () => void;
  /** Same options as the Lab session dropdown (`GET /presets` → sessions, plus All). */
  sessionOptions?: StrategyRdSessionPickerGroup[];
  /** Used to show catalog names in TES replies instead of concept ids. */
  catalog?: StrategyRdCatalog | null;
  onOrientationChip: (chip: StrategyRdCoachChip) => void;
  onStyleClick: (styleId: string) => void;
  onWow: () => void;
  onSend: (text?: string) => void;
  onConfirmPatch: () => void;
  onDismissPatch: () => void;
  onSelectThread: (id: string) => void;
  onNewThread: () => void;
  onDeleteThread: (id: string) => void;
  onConfirmRestore: () => void;
  onDismissRestore: () => void;
  onChipClick: (chip: StrategyRdCoachChip) => void;
};

/** Outline-brand chip: style seeds and common questions (same look as follow-up chips). */
function CoachOutlinePill({
  label,
  disabled,
  title,
  onClick,
}: {
  label: string;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline-brand"
      size="sm"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={COACH_CHIP_CLASS}
    >
      <span>{label}</span>
    </Button>
  );
}

/** The Lab's own quick links get their own icon; anything else is a TES suggestion. */
const CHIP_ICONS: Record<string, typeof Sparkles> = {
  lab_open_recipe: BookOpen,
  lab_open_backtest_settings: SlidersHorizontal,
  lab_run_preview: Eye,
  lab_review_backtest: ScanSearch,
};

function ChipIcon({ id }: { id: string }) {
  const Icon = CHIP_ICONS[id] ?? Sparkles;
  return <Icon className="size-3.5 shrink-0" aria-hidden />;
}

/** Clickable coach suggestions: the same outline-brand chip as the style seeds. */
function ChipRow({
  chips,
  disabled,
  onClick,
}: {
  chips: StrategyRdCoachChip[];
  disabled?: boolean;
  onClick: (chip: StrategyRdCoachChip) => void;
}) {
  if (!chips.length) return null;
  const unique = chips.filter(
    (chip, i, arr) =>
      arr.findIndex((c) => c.id === chip.id && c.label === chip.label) === i
  );
  return (
    <div className="flex flex-wrap gap-2" data-strategy-rd-coach-followups="">
      {unique.slice(0, 4).map((chip) => (
        <Button
          key={chip.id + chip.label}
          type="button"
          variant="outline-brand"
          size="sm"
          disabled={disabled}
          onClick={() => onClick(chip)}
          className={COACH_CHIP_CLASS}
        >
          <ChipIcon id={chip.id} />
          <span className="min-w-0">{chip.label}</span>
        </Button>
      ))}
    </div>
  );
}

/** Strip react-markdown's ``node`` prop before spreading the rest onto a DOM element. */
type MdProps<T extends keyof JSX.IntrinsicElements> = JSX.IntrinsicElements[T] & { node?: unknown };
function domProps<T extends { node?: unknown }>(props: T): Omit<T, "node"> {
  const { node: _node, ...rest } = props;
  return rest;
}

/** Headings inside a reply's markdown: h1/h2 as brand titles, h3 and below in the app's headline style. */
const COACH_MD_TITLE_CLASS = cn(COACH_TITLE_CLASS, "!m-0 !mt-1 first:!mt-0");
// Every heading level is a brand title: no bullets, no small caps, just Tropical Surf above grey.
const COACH_MD_SUBHEAD_CLASS = cn(COACH_TITLE_CLASS, "!m-0 !mt-1 first:!mt-0 !text-sm");
// Lists read as spaced lines: no bullet markers or numbers (nested ones indent a little).
const COACH_LIST_CLASS = "!m-0 flex flex-col gap-2 !p-0 !list-none [&_div[data-coach-list]]:!pl-3";

/**
 * react-markdown renderers: every block gets its spacing from the wrapper's gap (Chakra's reset
 * zeroes element margins, so nothing relies on them), tables render as the app's table card.
 */
const COACH_MARKDOWN_COMPONENTS: Components = {
  p: (props: MdProps<"p">) => <p {...domProps(props)} className="!m-0" />,
  h1: (props: MdProps<"h1">) => <h3 {...domProps(props)} className={COACH_MD_TITLE_CLASS} />,
  h2: (props: MdProps<"h2">) => <h3 {...domProps(props)} className={COACH_MD_TITLE_CLASS} />,
  h3: (props: MdProps<"h3">) => <h4 {...domProps(props)} className={COACH_MD_SUBHEAD_CLASS} />,
  h4: (props: MdProps<"h4">) => <h4 {...domProps(props)} className={COACH_MD_SUBHEAD_CLASS} />,
  h5: (props: MdProps<"h5">) => <h4 {...domProps(props)} className={COACH_MD_SUBHEAD_CLASS} />,
  h6: (props: MdProps<"h6">) => <h4 {...domProps(props)} className={COACH_MD_SUBHEAD_CLASS} />,
  ul: ({ children }) => <div data-coach-list="" className={COACH_LIST_CLASS}>{children}</div>,
  ol: ({ children }) => <div data-coach-list="" className={COACH_LIST_CLASS}>{children}</div>,
  li: ({ children }) => <div className="leading-6 [&>p]:!m-0 [&>p+p]:!mt-1.5">{children}</div>,
  strong: ({ children }) => <CoachLabel>{children}</CoachLabel>,
  em: ({ children }) => <em className="italic text-gray-600 dark:text-foreground">{children}</em>,
  code: ({ children }) => <CoachTerm>{children}</CoachTerm>,
  pre: ({ children }) => (
    <pre
      className={cn(
        "!m-0 overflow-x-auto rounded-[6px] !border !border-solid !border-border bg-muted/50 !px-3 !py-2",
        "font-[family-name:var(--font-secondary)] text-[0.8125rem] leading-5 text-gray-600 dark:text-foreground",
        "[&>span]:!border-0 [&>span]:!bg-transparent [&>span]:!p-0 [&>span]:whitespace-pre"
      )}
    >
      {children}
    </pre>
  ),
  a: (props: MdProps<"a">) => (
    <a
      {...domProps(props)}
      target="_blank"
      rel="noreferrer"
      className="text-[color:var(--brand-tropical-surf)] underline-offset-2 hover:underline"
    />
  ),
  blockquote: (props: MdProps<"blockquote">) => (
    <blockquote
      {...domProps(props)}
      className="!m-0 flex flex-col gap-2 !border-0 !border-l-2 !border-solid !border-[color:var(--brand-tropical-surf)]/40 !pl-3 [&>p]:!m-0"
    />
  ),
  hr: () => <hr className="!m-0 !border-0 !border-t !border-solid !border-border" />,
  table: ({ children }) => (
    <div
      className="overflow-hidden rounded-[8px] !border !border-solid !border-border bg-card"
      data-strategy-rd-coach-table=""
    >
      <Table className="!border-collapse font-[family-name:var(--font-primary)] text-[0.8125rem]">{children}</Table>
    </div>
  ),
  thead: ({ children }) => (
    <TableHeader className="bg-muted/50 [&_tr]:!border-0 [&_tr]:!border-b [&_tr]:!border-solid [&_tr]:!border-border [&_tr]:hover:bg-transparent">
      {children}
    </TableHeader>
  ),
  tbody: ({ children }) => (
    <TableBody className="[&>tr]:!border-0 [&>tr]:!border-b [&>tr]:!border-solid [&>tr]:!border-border/70 [&>tr:last-child]:!border-b-0">
      {children}
    </TableBody>
  ),
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: (props: MdProps<"th">) => (
    <TableHead
      style={props.style}
      className={cn(
        "!h-9 !px-3 !py-0 font-[family-name:var(--font-primary)] text-xs !font-medium text-gray-500 dark:text-muted-foreground"
      )}
    >
      {props.children}
    </TableHead>
  ),
  td: (props: MdProps<"td">) => (
    <TableCell
      style={props.style}
      className="!px-3 !py-2 align-top !whitespace-normal font-[family-name:var(--font-primary)] text-[0.8125rem] leading-5 tabular-nums text-gray-500 dark:text-muted-foreground"
    >
      {props.children}
    </TableCell>
  ),
};

function CoachMarkdown({ text, catalog }: { text: string; catalog?: StrategyRdCatalog | null }) {
  if (!text.trim()) return null;
  return (
    <div className={cn("flex min-w-0 max-w-none flex-col gap-3 break-words", COACH_BODY_TEXT_CLASS)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={COACH_MARKDOWN_COMPONENTS}>
        {strategyRdCoachReplyForDisplay(text, catalog)}
      </ReactMarkdown>
    </div>
  );
}

/**
 * One TES reply, laid out: its sections, the strategy it tested as a card, the backtest card,
 * then its "Next step" as a TES suggests box. Same text, same buttons; only the layout differs.
 */
function CoachReply({
  message,
  catalog,
  chipsDisabled,
  onChipClick,
  onOpenHypothesis,
  onCompareHypothesis,
}: {
  message: StrategyRdCoachUiMessage;
  catalog?: StrategyRdCatalog | null;
  chipsDisabled?: boolean;
  onChipClick: (chip: StrategyRdCoachChip) => void;
  onOpenHypothesis?: (hypothesisId: string) => void;
  onCompareHypothesis?: (hypothesisId: string) => void;
}) {
  const parts = useMemo(() => strategyRdCoachReplyParts(message.content), [message.content]);
  const chips = useMemo(
    () => strategyRdCoachChipsForTurn(message.suggested_chips ?? []).map(strategyRdCoachChipForDisplay),
    [message.suggested_chips]
  );
  const result = message.lab_actions?.find((a) => a.card)?.card;
  const isNext = (h: string | null) => h != null && /^next steps?$/i.test(h);
  const next = parts.sections.find((sec) => isNext(sec.heading));
  return (
    <div className="flex flex-col gap-5">
      {parts.sections
        .filter((sec) => !isNext(sec.heading))
        .map((sec, i) => (
          <section key={i} className="flex flex-col gap-2">
            {sec.heading ? <h3 className={cn(COACH_TITLE_CLASS, "!m-0")}>{sec.heading}</h3> : null}
            <CoachMarkdown text={sec.body} catalog={catalog} />
          </section>
        ))}
      {parts.strategy ? (
        <StrategyRdCoachStrategyCard
          strategy={parts.strategy}
          name={result?.name}
          version={result?.version}
          chips={chips}
          chipsDisabled={chipsDisabled}
          onChipClick={onChipClick}
        />
      ) : null}
      {message.lab_actions?.length ? (
        <div className="flex flex-col gap-5">
          {message.lab_actions.map((action) => (
            <StrategyRdCoachLabActionCard
              key={action.job_id}
              action={action}
              onOpen={onOpenHypothesis}
              onCompare={onCompareHypothesis}
            />
          ))}
        </div>
      ) : null}
      {next ? (
        <div
          className="flex flex-col gap-2 rounded-[8px] !border !border-solid !border-border bg-card !px-4 !py-3.5"
          data-strategy-rd-coach-suggests=""
        >
          <p className={cn(statsCardHeadlineClassName, "!m-0 flex items-center gap-1.5 !text-[color:var(--brand-tropical-surf)]")}>
            <Sparkles className="size-3.5" aria-hidden />
            TES suggests
          </p>
          <CoachMarkdown text={next.body} catalog={catalog} />
        </div>
      ) : null}
    </div>
  );
}

/** Sent when only a screenshot is attached (the BE needs a message). */
const IMAGE_ONLY_MESSAGE = "Build this from my chart.";

function CoachComposerInput({
  valueFromParent,
  sending,
  quotaBlocked,
  placeholder,
  onSend,
  attachments = [],
  onAddImages,
  onRemoveAttachment,
  chartFocusLabel = null,
  onClearChartFocus,
  rotatingPrompts,
}: {
  valueFromParent: string;
  sending: boolean;
  quotaBlocked: boolean;
  placeholder: string;
  onSend: (text?: string) => void;
  attachments?: StrategyRdCoachAttachment[];
  onAddImages?: (files: File[]) => void;
  onRemoveAttachment?: (id: string) => void;
  chartFocusLabel?: string | null;
  onClearChartFocus?: () => void;
  /** Empty chat: example asks cycled as the placeholder; Tab puts the shown one in the box. */
  rotatingPrompts?: string[];
}) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(valueFromParent);
  const [focused, setFocused] = useState(false);
  const [promptIndex, setPromptIndex] = useState(0);
  const [promptHidden, setPromptHidden] = useState(false);
  const prompts = rotatingPrompts ?? [];
  const suggestion = !draft && prompts.length > 0 ? prompts[promptIndex % prompts.length] : null;

  // Cycle the example asks while the box is idle; hold the shown one once it has focus so Tab takes it.
  // Fade the shown one out, swap it while hidden, fade the next one in.
  useEffect(() => {
    if (prompts.length < 2 || draft || focused) return;
    let swap: number | undefined;
    const id = window.setInterval(() => {
      setPromptHidden(true);
      swap = window.setTimeout(() => {
        setPromptIndex((i) => i + 1);
        setPromptHidden(false);
      }, 300);
    }, 3500);
    return () => {
      window.clearInterval(id);
      window.clearTimeout(swap);
      setPromptHidden(false);
    };
  }, [prompts.length, draft, focused]);

  useEffect(() => {
    setDraft(valueFromParent);
  }, [valueFromParent]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "0px";
    const next = Math.min(el.scrollHeight, 112);
    el.style.height = `${Math.max(next, 36)}px`;
  }, [draft]);

  const submit = () => {
    const text = draft.trim() || (attachments.length > 0 ? IMAGE_ONLY_MESSAGE : "");
    if (!text || sending || quotaBlocked) return;
    setDraft("");
    onSend(text);
  };

  return (
    <div className="flex flex-col gap-2">
      {chartFocusLabel || attachments.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {chartFocusLabel ? (
            <span
              className="inline-flex h-7 items-center gap-1.5 rounded-[6px] !border !border-solid !border-[color:var(--brand-tropical-surf)]/40 !px-2 font-[family-name:var(--font-primary)] text-xs text-[color:var(--brand-tropical-surf)]"
              data-strategy-rd-chart-focus=""
            >
              <Crosshair className="size-3" aria-hidden />
              {chartFocusLabel}
              {onClearChartFocus ? (
                <button
                  type="button"
                  aria-label="Don't send this candle"
                  className="inline-flex items-center rounded-[4px] opacity-70 hover:opacity-100"
                  onClick={onClearChartFocus}
                >
                  <X className="size-3" aria-hidden />
                </button>
              ) : null}
            </span>
          ) : null}
          {attachments.map((a) => (
            <span key={a.id} className="relative inline-block" data-strategy-rd-attachment="">
              <img src={a.preview} alt="Attached chart" className="h-12 w-16 rounded-[6px] !border !border-solid !border-border object-cover" />
              {onRemoveAttachment ? (
                <button
                  type="button"
                  aria-label="Remove image"
                  className="absolute -right-1.5 -top-1.5 inline-flex size-5 items-center justify-center rounded-[4px] !border !border-solid !border-border bg-background text-gray-500 hover:text-foreground"
                  onClick={() => onRemoveAttachment(a.id)}
                >
                  <X className="size-3.5" aria-hidden />
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}
    {/* Wide: one row (attach · text · Tab · send). Narrow: text and Tab on top, attach and send below.
        Box styling mirrors the app's InputGroup / Textarea: 6px radius, input border, ring on focus. */}
    <div className="@container/composer">
    <div
      data-strategy-rd-coach-composer=""
      className={cn(
        "flex flex-col gap-1.5 rounded-[6px] !border !border-solid !border-input bg-background !p-1.5 transition-[color,box-shadow,border-color] dark:bg-input/30",
        "@[20rem]/composer:flex-row @[20rem]/composer:items-end @[20rem]/composer:gap-1.5",
        "focus-within:!border-[color:var(--brand-tropical-surf)]/60 focus-within:ring-3 focus-within:ring-[color:var(--brand-tropical-surf)]/15",
        "has-[textarea:disabled]:opacity-80"
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-2">
        <div className="relative min-w-0 flex-1">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label={suggestion ?? placeholder}
            disabled={sending || quotaBlocked}
            rows={1}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            className={cn(
              "block max-h-28 min-h-[2.25rem] w-full !resize-none overflow-y-auto bg-transparent !px-2 !py-1.5",
              "!border-0 font-[family-name:var(--font-primary)] !text-sm !leading-6 !text-gray-600 outline-none dark:!text-foreground",
              "disabled:opacity-60"
            )}
            onKeyDown={(e) => {
              if (e.key === "Tab" && !e.shiftKey && suggestion) {
                e.preventDefault();
                setDraft(suggestion);
                return;
              }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            onPaste={(e) => {
              if (!onAddImages) return;
              const files = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith("image/"));
              if (files.length === 0) return;
              e.preventDefault();
              onAddImages(files);
            }}
          />
          {/* Placeholder on one line with an ellipsis (a native textarea placeholder wraps or scrolls). */}
          {!draft ? (
            <span
              key={suggestion ?? placeholder}
              className={cn(
                "pointer-events-none absolute inset-x-0 top-0 truncate px-2 py-1.5 font-[family-name:var(--font-primary)] text-sm leading-6 text-muted-foreground",
                "transition-[opacity,transform] ease-in motion-reduce:transform-none",
                "duration-300",
                suggestion && !promptHidden && "animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none",
                suggestion && promptHidden && "-translate-y-1 opacity-0"
              )}
              aria-hidden
            >
              {suggestion ?? placeholder}
            </span>
          ) : null}
        </div>
        {suggestion ? (
          <kbd
            className={cn(
              "mt-[7px] shrink-0 !rounded-[4px] !border !border-solid !border-border bg-muted/50 !px-1.5",
              "font-[family-name:var(--font-secondary)] font-normal text-gray-400 shadow-[0_1px_0_var(--border)]"
            )}
            style={{ fontSize: 10, lineHeight: "18px" }}
            title="Press Tab to use this suggestion"
            aria-hidden
          >
            Tab
          </kbd>
        ) : null}
      </div>
      <div className="flex items-center justify-between gap-2 @[20rem]/composer:contents">
      {onAddImages ? (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) onAddImages(files);
              e.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="size-9 shrink-0 rounded-[6px] !p-0 text-gray-500 hover:bg-muted hover:text-[color:var(--brand-tropical-surf)] @[20rem]/composer:order-first [&_svg]:text-gray-500 hover:[&_svg]:text-[color:var(--brand-tropical-surf)]"
            disabled={sending || quotaBlocked}
            onClick={() => fileRef.current?.click()}
            aria-label="Attach a chart screenshot"
            title="Attach or paste a chart screenshot (PNG, JPEG, WebP · up to 2)"
          >
            <ImagePlus className="size-4" aria-hidden />
          </Button>
        </>
      ) : (
        <span aria-hidden />
      )}
      {/* The app's gradient button: rectangular, same 36px height as the box's other controls. */}
      <Button
        type="button"
        variant="gradient"
        size="icon-sm"
        data-strategy-rd-coach-send=""
        className="size-9 shrink-0 rounded-[6px] !p-0"
        disabled={sending || quotaBlocked || (!draft.trim() && attachments.length === 0)}
        onClick={submit}
        aria-label="Send"
      >
        <Send className="size-4" />
      </Button>
      </div>
    </div>
    </div>
    </div>
  );
}

export function StrategyRdCoachPanel(props: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const isEmpty = !props.bootstrapping && props.messages.length === 0;
  const { user } = useClerkAuth();
  const firstName = user?.firstName || user?.lastName || "";
  const rotatingPrompts = useMemo(() => rotatingPromptsFromStyles(props.styles), [props.styles]);
  /** Most recent earlier chat with messages — "pick up where you left off". */
  const aboutBacktest = isEmpty ? props.backtestChat ?? null : null;
  const threadTitle = aboutBacktest
    ? aboutBacktest.name
    : isEmpty
      ? "New chat"
      : props.threads.find((t) => t.id === props.sessionId)?.title || STRATEGY_RD_COACH_DISPLAY_NAME;
  const latestThread = useMemo(
    () => props.threads.find((t) => t.id !== props.sessionId && (t.message_count ?? 0) > 0) ?? null,
    [props.threads, props.sessionId]
  );

  const lastAssistantChips = useMemo(() => {
    const asked = props.messages
      .filter((m) => m.role === "user")
      .map((m) => m.content);
    for (let i = props.messages.length - 1; i >= 0; i -= 1) {
      const m = props.messages[i];
      if (m.role !== "assistant" || !m.suggested_chips?.length) continue;
      const chips = strategyRdCoachChipsExceptAsked(
        strategyRdCoachChipsForTurn(m.suggested_chips).map(strategyRdCoachChipForDisplay),
        asked
      );
      if (chips.length === 0) return null;
      return { messageId: m.id, chips };
    }
    return null;
  }, [props.messages]);

  // An opened backtest scrolls to the reply that ran it (below). Until the next message, later
  // updates (a job card's status) mustn't pull the chat back to the bottom.
  const openedMessageId = props.openedBacktest?.messageId ?? null;
  const openedToken = props.openedBacktest?.token ?? null;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || (openedMessageId && !props.sending)) return;
    el.scrollTop = el.scrollHeight;
  }, [props.messages, props.pendingProposal, props.sending, openedMessageId]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !openedMessageId) return;
    const target = el.querySelector<HTMLElement>(`[data-strategy-rd-coach-message="${CSS.escape(openedMessageId)}"]`);
    target?.scrollIntoView({ block: "start" });
  }, [openedMessageId, openedToken]);
  const openedChips = props.openedBacktestChips ?? [];

  return (
    <aside
      data-strategy-rd-coach-panel=""
      className={cn(
        "@container/coach relative flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden",
        // Lab tram lines: the column's own border-r separates it from the chart — no card.
        "bg-background text-card-foreground"
      )}
      style={{ width: "100%" }}
      onWheel={(e) => e.stopPropagation()}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize TES panel"
        onPointerDown={(e) => {
          e.preventDefault();
          props.onResizeStart(e.clientX);
        }}
        className={cn(
          "absolute inset-y-0 z-20 w-1 cursor-col-resize bg-transparent hover:bg-[color:var(--brand-tropical-surf)]",
          props.dock === "left" ? "right-0" : "left-0"
        )}
      />

      {/* Chat actions left, the chat's title centred, the weekly allowance and minimize right. */}
      <header className="relative z-10 grid shrink-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="size-8 rounded-[6px] !p-0 text-gray-500 [&_svg]:text-gray-500"
            aria-label={historyOpen ? "Hide chat history" : "Show chat history"}
            onClick={() => setHistoryOpen((v) => !v)}
          >
            <History className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="size-8 rounded-[6px] !p-0 text-gray-500 [&_svg]:text-gray-500"
            aria-label="New chat"
            disabled={props.threadsBusy}
            onClick={props.onNewThread}
          >
            <SquarePen className="size-4" />
          </Button>
        </div>
        <h3
          className="m-0 truncate text-center font-[family-name:var(--font-primary)] !text-[13px] font-normal text-gray-400"
          title={`${STRATEGY_RD_COACH_DISPLAY_NAME} · ${STRATEGY_RD_COACH_DISPLAY_EXPANSION}`}
          data-strategy-rd-coach-title=""
        >
          {threadTitle}
        </h3>
        <div className="flex min-w-0 items-center gap-1">
          {/* Weekly usage shows as rings in the top bar now. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="size-8 shrink-0 rounded-[6px] !p-0 text-gray-500 [&_svg]:text-gray-500"
            aria-label="Minimize TES"
            onClick={props.onMinimize}
          >
            <Minimize2 className="size-4" />
          </Button>
        </div>
      </header>

      {historyOpen ? (
        <div className="absolute inset-y-0 left-0 z-30 flex w-[min(100%,16.5rem)] flex-col border-r border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
            <p className={cn(statsCardHeadlineClassName, "!m-0")}>Recents</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-7 rounded-[6px] !p-0 text-gray-500"
              aria-label="Close history"
              onClick={() => setHistoryOpen(false)}
            >
              <X className="size-3.5" />
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
            <StrategyRdCoachThreads
              threads={props.threads}
              activeId={props.sessionId}
              busy={props.threadsBusy}
              onSelect={(id) => {
                props.onSelectThread(id);
                setHistoryOpen(false);
              }}
              onNew={() => {
                props.onNewThread();
                setHistoryOpen(false);
              }}
              onDelete={props.onDeleteThread}
            />
          </div>
        </div>
      ) : null}

      <>
          {props.pendingRestore ? (
            <div className="mx-5 mt-3 flex shrink-0 flex-col gap-2 rounded-[8px] !border !border-solid !border-[color:var(--brand-tropical-surf)]/35 bg-card !px-4 !py-3">
              <p className={cn(COACH_TITLE_CLASS, "!m-0 !text-sm")}>
                This chat saved a different recipe.
              </p>
              <p className="!m-0 font-[family-name:var(--font-primary)] text-xs leading-5 text-gray-500 dark:text-muted-foreground">
                Load that setup, confluences, and entry into the Lab, or keep the recipe you have now. The chat stays either way.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="gradient"
                  size="sm"
                  data-font="primary"
                  className={cn(
                    strategyRdBrandActionButtonClassName,
                    "h-8 px-3 text-[0.75rem] normal-case tracking-normal"
                  )}
                  onClick={props.onConfirmRestore}
                >
                  <span className="normal-case tracking-normal">Load this recipe</span>
                </Button>
                <Button
                  type="button"
                  variant="outline-gray"
                  size="sm"
                  data-font="primary"
                  className={cn(
                    strategyRdBrandActionButtonClassName,
                    "h-8 px-3 text-[0.75rem] normal-case tracking-normal"
                  )}
                  onClick={props.onDismissRestore}
                >
                  <span className="normal-case tracking-normal">Keep current</span>
                </Button>
              </div>
            </div>
          ) : null}

          <div className="relative flex min-h-0 flex-1 flex-col">
            <div
              ref={scrollRef}
              className="relative z-[2] min-h-0 flex-1 overflow-y-auto overscroll-contain px-5"
            >
            <div className={isEmpty ? "flex min-h-full flex-col justify-center" : undefined}>
              {props.bootstrapping ? (
                <div className="flex min-h-[12rem] items-center justify-center py-8">
                  <p className="font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
                    Loading TES…
                  </p>
                </div>
              ) : isEmpty ? (
                <div className="flex min-h-full flex-col items-center justify-center gap-6 py-10" data-strategy-rd-coach-empty="">
                  <div className="flex flex-col items-center gap-2 text-center @[30rem]/coach:flex-row @[30rem]/coach:gap-3 @[30rem]/coach:text-left">
                    <img
                      src={SIDEBAR_BRAND_LOGO_SRC}
                      alt=""
                      width={36}
                      height={36}
                      className="size-9 shrink-0 object-contain"
                      draggable={false}
                    />
                    {aboutBacktest ? (
                      <div className="flex min-w-0 flex-col gap-1">
                        <h4
                          className={cn(
                            "m-0 font-[family-name:var(--font-primary)] font-medium tracking-tight text-[color:var(--brand-tropical-surf)]",
                            "!text-[20px] !leading-7 @[30rem]/coach:!text-[24px] @[30rem]/coach:!leading-8"
                          )}
                          data-strategy-rd-coach-backtest-title=""
                        >
                          Ask about {aboutBacktest.name}
                        </h4>
                        <p className={cn(COACH_BODY_TEXT_CLASS, "!m-0")}>
                          {STRATEGY_RD_COACH_DISPLAY_NAME} has this backtest open: its results, trades and rules.
                        </p>
                      </div>
                    ) : (
                      <h4
                        className={cn(
                          "m-0 font-[family-name:var(--font-primary)] font-medium tracking-tight text-[color:var(--brand-tropical-surf)]",
                          "!text-[22px] !leading-7 @[30rem]/coach:!text-[28px] @[30rem]/coach:!leading-[34px]"
                        )}
                      >
                        {timeOfDayGreeting()}
                        {firstName ? `, ${firstName}` : ""}
                      </h4>
                    )}
                  </div>
                  <div className="w-full max-w-[40rem]">
                    <CoachComposerInput
                      valueFromParent={props.input}
                      sending={props.sending}
                      quotaBlocked={props.quotaBlocked}
                      placeholder={
                        aboutBacktest
                          ? `Ask about ${aboutBacktest.name}…`
                          : `Ask ${STRATEGY_RD_COACH_DISPLAY_NAME}…`
                      }
                      onSend={props.onSend}
                      attachments={props.attachments}
                      onAddImages={props.onAddImages}
                      onRemoveAttachment={props.onRemoveAttachment}
                      chartFocusLabel={
                        props.chartFocus
                          ? `About ${formatTradeDateTimeForDisplay(props.chartFocus.time, props.timeZone)}`
                          : null
                      }
                      onClearChartFocus={props.onClearChartFocus}
                      rotatingPrompts={aboutBacktest ? undefined : rotatingPrompts}
                    />
                  </div>
                  {aboutBacktest ? (
                    <div className="flex w-full max-w-[28rem] flex-col gap-2" data-strategy-rd-coach-backtest-prompts="">
                      {BACKTEST_CHAT_PROMPTS.map((prompt) => (
                        <button
                          key={prompt}
                          type="button"
                          disabled={props.sending || props.quotaBlocked}
                          onClick={() => props.onSend(prompt)}
                          className={cn(
                            "group flex w-full items-center gap-3 !rounded-[8px] !border !border-solid !border-border !bg-background !px-4 !py-3 text-left",
                            "transition-colors hover:!border-[color:var(--brand-tropical-surf)]/50 disabled:opacity-50"
                          )}
                        >
                          <MessageSquare className="size-4 shrink-0 text-gray-400" aria-hidden />
                          <span className="min-w-0 flex-1 font-[family-name:var(--font-primary)] text-sm text-gray-600 dark:text-foreground">
                            {prompt}
                          </span>
                          <ChevronRight
                            className="size-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-[color:var(--brand-tropical-surf)]"
                            aria-hidden
                          />
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {!aboutBacktest && props.styles.length > 0 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          disabled={props.intakeBusy}
                          className={cn(
                            "inline-flex min-w-0 max-w-full items-center gap-2 !rounded-[8px] !border !border-solid !border-border !bg-background !px-3 !py-2",
                            "font-[family-name:var(--font-primary)] !text-sm !text-gray-500 transition-colors",
                            "hover:!border-[color:var(--brand-tropical-surf)]/50 disabled:opacity-50"
                          )}
                          data-strategy-rd-coach-style-picker=""
                        >
                          <Layers className="size-4 shrink-0 text-gray-400" aria-hidden />
                          <span className="min-w-0 truncate">
                            Try <span className="font-semibold text-[color:var(--brand-tropical-surf)]">a style</span>
                          </span>
                          <span className="h-4 w-px shrink-0 bg-border" aria-hidden />
                          <ChevronDown className="size-4 shrink-0 text-gray-400" aria-hidden />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center" data-strategy-rd-style-menu="">
                        <DropdownMenuLabel className="px-2 py-1 text-xs font-normal text-gray-400">
                          Start from a style
                        </DropdownMenuLabel>
                        {props.styles.slice(0, 6).map((style) => (
                          <DropdownMenuItem
                            key={style.id}
                            data-strategy-rd-style-item=""
                            onSelect={() => props.onStyleClick(style.id)}
                          >
                            <span className="font-[family-name:var(--font-primary)] text-sm text-gray-600 dark:text-foreground">
                              {style.name}
                            </span>
                            {style.blurb || style.description ? (
                              <span className="line-clamp-1 font-[family-name:var(--font-primary)] text-xs text-gray-400">
                                {style.blurb || style.description}
                              </span>
                            ) : null}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="gap-2 rounded-[6px] px-2 py-2"
                          onSelect={props.onWow}
                        >
                          <Sparkles className="size-4 text-[color:var(--brand-tropical-surf)]" aria-hidden />
                          <span className="font-[family-name:var(--font-primary)] text-sm text-[color:var(--brand-tropical-surf)]">
                            Surprise me
                          </span>
                          <span className="font-[family-name:var(--font-primary)] text-xs text-gray-400">Try wow</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                  {latestThread && !aboutBacktest ? (
                    <button
                      type="button"
                      disabled={props.threadsBusy}
                      onClick={() => props.onSelectThread(latestThread.id)}
                      className={cn(
                        "group flex w-full max-w-[28rem] items-center gap-3 !rounded-[8px] !border !border-solid !border-border !bg-background !px-4 !py-3 text-left",
                        "transition-colors hover:!border-[color:var(--brand-tropical-surf)]/50"
                      )}
                      data-strategy-rd-coach-latest=""
                    >
                      <MessageSquare className="size-4 shrink-0 text-gray-400" aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="block font-[family-name:var(--font-primary)] text-xs text-gray-400">
                          Pick up where you left off
                        </span>
                        <span className="block truncate font-[family-name:var(--font-primary)] text-sm text-gray-600 dark:text-foreground">
                          {latestThread.title}
                        </span>
                      </span>
                      <ChevronRight
                        className="size-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-[color:var(--brand-tropical-surf)]"
                        aria-hidden
                      />
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="flex flex-col gap-8 py-6">
                  {props.messages.map((msg) => (
                    <div
                      key={msg.id}
                      data-strategy-rd-coach-message={msg.id}
                      data-role={msg.role}
                      className={cn(
                        "min-w-0 scroll-mt-4 font-[family-name:var(--font-primary)] text-sm leading-6",
                        msg.role === "user" ? "ml-auto max-w-[85%]" : "mr-auto w-full",
                        // The reply an opened backtest came from: a quiet brand outline, no fill.
                        msg.id === openedMessageId &&
                          "-mx-3 w-[calc(100%+1.5rem)] max-w-none rounded-[8px] !px-3 !py-3 ring-1 ring-[color:var(--brand-tropical-surf)]/35 bg-[color:var(--brand-tropical-surf)]/[0.03]"
                      )}
                      data-strategy-rd-coach-opened={msg.id === openedMessageId ? "" : undefined}
                    >
                      {msg.role === "user" ? (
                        <div className="rounded-[8px] bg-muted !px-3.5 !py-2.5 text-gray-600 dark:text-foreground">
                          {msg.image_previews?.length ? (
                            <div className="mb-2 flex flex-wrap gap-1.5">
                              {msg.image_previews.map((src, i) => (
                                <img key={i} src={src} alt="Attached chart" className="h-14 w-20 rounded-[6px] object-cover" />
                              ))}
                            </div>
                          ) : null}
                          <p className="!m-0 whitespace-pre-wrap break-words">
                            {strategyRdCoachUserTextForDisplay(msg.content)}
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-2">
                            <img
                              src={SIDEBAR_BRAND_LOGO_SRC}
                              alt=""
                              width={16}
                              height={16}
                              className="size-4 shrink-0 object-contain"
                              draggable={false}
                            />
                            <span className={statsCardHeadlineClassName}>{STRATEGY_RD_COACH_DISPLAY_NAME}</span>
                          </div>
                          {msg.activity ? <StrategyRdCoachTurnTraceView trace={msg.activity} /> : null}
                          <CoachReply
                            message={msg}
                            catalog={props.catalog}
                            chipsDisabled={props.sending || props.intakeBusy}
                            onChipClick={props.onChipClick}
                            onOpenHypothesis={props.onOpenHypothesis}
                            onCompareHypothesis={props.onCompareHypothesis}
                          />
                          {msg.proposal?.resolved ? (
                            <p className={cn(COACH_CAPTION_CLASS, "!m-0 flex items-center gap-1.5")}>
                              {msg.proposal.resolved === "confirmed" ? "Changes applied to the Lab." : "Changes dismissed."}
                            </p>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ))}
                  {props.sending ? (
                    <StrategyRdCoachLiveActivity activity={props.activity ?? null} logoSrc={SIDEBAR_BRAND_LOGO_SRC} />
                  ) : null}
                </div>
              )}
            </div>
            </div>
          </div>

          {props.pendingProposal ? (
            <div className="shrink-0 px-5 pb-3">
              <StrategyRdCoachConfirmApply
                proposal={props.pendingProposal}
                confirming={props.confirmingPatch}
                onConfirm={props.onConfirmPatch}
                onDismiss={props.onDismissPatch}
              />
            </div>
          ) : null}

          {/* Footer: suggestions, then the input, then the disclaimer, each on its own gap. */}
          <div className="flex shrink-0 flex-col gap-3 px-5 pb-4 pt-3" data-strategy-rd-coach-footer="">
            {openedChips.length > 0 && props.openedBacktest && !props.sending ? (
              <div className="flex flex-col gap-2" data-strategy-rd-coach-opened-backtest="">
                <p className={cn(COACH_CAPTION_CLASS, "!m-0 truncate")}>
                  {strategyRdCoachOpenedBacktestLabel(props.openedBacktest)}
                </p>
                <ChipRow chips={openedChips} disabled={props.sending || props.intakeBusy} onClick={props.onChipClick} />
              </div>
            ) : !isEmpty && lastAssistantChips && !props.sending ? (
              <ChipRow
                chips={lastAssistantChips.chips}
                disabled={props.sending || props.intakeBusy}
                onClick={props.onChipClick}
              />
            ) : null}
            {props.intakeOpen && !isEmpty ? (
              <div className="flex flex-col gap-2" data-strategy-rd-intake="">
                <div className="flex items-center justify-between gap-2">
                  <p className={cn(COACH_CAPTION_CLASS, "!m-0")}>Start a strategy from a style</p>
                  {props.onCloseIntake ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="size-7 rounded-[6px] !p-0 text-gray-500"
                      aria-label="Close"
                      onClick={props.onCloseIntake}
                    >
                      <X className="size-3.5" />
                    </Button>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {props.styles.slice(0, 6).map((style) => (
                    <CoachOutlinePill
                      key={style.id}
                      label={style.name}
                      title={style.blurb || style.description || undefined}
                      disabled={props.intakeBusy}
                      onClick={() => props.onStyleClick(style.id)}
                    />
                  ))}
                </div>
              </div>
            ) : null}
            {props.quotaBlocked ? (
              <p className="!m-0 font-[family-name:var(--font-primary)] text-xs font-medium text-gray-600 dark:text-foreground">
                Weekly turn quota used. Style seeds stay free.
              </p>
            ) : null}
            {!isEmpty ? (
              <CoachComposerInput
                valueFromParent={props.input}
                sending={props.sending}
                quotaBlocked={props.quotaBlocked}
                placeholder={props.sending ? `${STRATEGY_RD_COACH_DISPLAY_NAME} is working…` : "Ask a follow-up…"}
                onSend={props.onSend}
                attachments={props.attachments}
                onAddImages={props.onAddImages}
                onRemoveAttachment={props.onRemoveAttachment}
                chartFocusLabel={
                  props.chartFocus
                    ? `About ${formatTradeDateTimeForDisplay(props.chartFocus.time, props.timeZone)}`
                    : null
                }
                onClearChartFocus={props.onClearChartFocus}
              />
            ) : null}
            {isEmpty && (props.orientationChips?.length ?? 0) > 0 ? (
              <details className="group flex flex-col items-center" data-strategy-rd-coach-faq="">
                <summary className="cursor-pointer list-none text-center font-[family-name:var(--font-primary)] text-xs text-gray-400 hover:text-gray-500">
                  New to the Lab? <span className="text-[color:var(--brand-tropical-surf)]">Common questions</span>
                </summary>
                <div className="mt-3 flex flex-wrap justify-center gap-2">
                  {props.orientationChips!.map((chip) => (
                    <CoachOutlinePill
                      key={chip.id + chip.label}
                      label={chip.label}
                      disabled={props.sending || props.intakeBusy}
                      onClick={() => props.onOrientationChip(chip)}
                    />
                  ))}
                </div>
              </details>
            ) : null}
            {props.disclaimer ? (
              <p
                className="!m-0 !pt-0.5 text-center font-[family-name:var(--font-primary)] text-[11px] leading-4 text-gray-400 dark:text-muted-foreground"
                data-strategy-rd-coach-disclaimer=""
              >
                {props.disclaimer}
              </p>
            ) : null}
          </div>
      </>
    </aside>
  );
}

export function StrategyRdCoachHint() {
  return (
    <div className="flex items-start gap-3 rounded-[6px] border border-dashed border-border bg-background/60 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
          {STRATEGY_RD_COACH_DISPLAY_NAME}
          <span className="ml-1.5 font-normal text-muted-foreground">
            ({STRATEGY_RD_COACH_DISPLAY_EXPANSION})
          </span>
        </p>
        <p className="mt-0.5 font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
          Opens beside the Lab after you confirm the market window.{" "}
          <span className="text-muted-foreground/80">{STRATEGY_RD_COACH_CATALOG_BADGE}</span>
        </p>
      </div>
    </div>
  );
}
